"use client";
import { CIDADAO_STORAGE_KEY } from "@/app/(citizen)/votacao/modals/onboarding-modal/use-onboarding-controller";
import getCidadao from "@/app/actions/get-cidadao";
import postCidadao from "@/app/actions/post-cidadao";
import { Cidadao } from "@/types/cidadao";
import { Info, InfoMessages } from "@/types/user";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useUser } from "./user-provider";

interface CidadaoContextType {
  cidadao: Cidadao | null | undefined;
  likes: number;
  setLikes: Dispatch<SetStateAction<number>>;
  refetch: () => void;
  isLoading: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  setStoredCidadao: Dispatch<SetStateAction<string | null>>;
}

const CidadaoContext = createContext({} as CidadaoContextType);

export const CidadaoProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [likes, setLikes] = useState(0);
  const { userInfo, isUserError } = useUser();
  const isMessageError = !!(userInfo && (userInfo as InfoMessages)?.mensagens);
  const [storedCidadao, setStoredCidadao] = useState<string | null>(null);

  const cidadaoProp = {
    id_gov: (userInfo as Info)?.sub ?? storedCidadao ?? "",
    nome: (userInfo as Info)?.nus ?? "",
    email: !!(userInfo as Info)?.sub ? "" : storedCidadao,
    tipo_login: !!(userInfo as Info)?.sub ? "GOVBR": "VISITANTE",
  };

  const shouldPost = useMemo(
    () =>
      (!!userInfo && !!(userInfo as Info)?.sub && (userInfo as Info)?.nus !== "") ||
      (!!storedCidadao && storedCidadao !== ""),
    [userInfo, storedCidadao]
  );

  const {
    isPending: pPending,
    isLoading: pLoading,
    data: postData,
  } = useQuery({
    queryKey: ["postCidadao", cidadaoProp],
    queryFn: postCidadao,
    enabled: shouldPost,
  }) as UseQueryResult;
	
  const {
    data: cidadao,
    refetch,
    isPending: gPending,
    isLoading: gLoading,
  } = useQuery({
    queryKey: ["cidadao", (userInfo as Info)?.sub ?? storedCidadao ?? ""],
    queryFn: getCidadao,
    enabled: !!postData,
  }) as UseQueryResult<Cidadao>;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedCidadaoBytes = localStorage.getItem(CIDADAO_STORAGE_KEY);
      setStoredCidadao(storedCidadaoBytes ? atob(storedCidadaoBytes) : null);
    }
  }, []);

  useEffect(() => {
    if (cidadao) {
      setLikes(cidadao.problemas_apoiados.length);
    }
  }, [cidadao]);

  const isLoading =
    (shouldPost && (pPending || pLoading || gLoading || gPending)) &&
    !(isUserError || isMessageError);
    
  return (
    <CidadaoContext.Provider
      value={{
        cidadao,
        refetch,
        isLoading,
        likes,
        setLikes,
        setIsModalOpen,
        isModalOpen,
        setStoredCidadao
      }}
    >
      {children}
    </CidadaoContext.Provider>
  );
};

export const useCidadao = () => useContext(CidadaoContext);
